'use client';

import React, { useState, useRef, useEffect } from 'react';
import { FontAwesomeIcon as Icon } from '@fortawesome/react-fontawesome';
import { faCalendarRange, faCaretDown } from '@fortawesome/pro-solid-svg-icons';
import useFilterSets from 'src/hooks/filters/useFilterSets';
import { LOCATION } from 'src/hooks/filters/constants';
import { createPortal } from 'react-dom';
import { VOLUME_2022_2025, MIN_VOLUME_YEAR_STR, MAX_VOLUME_YEAR_STR } from 'src/components/constants';

const DateDropdown = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [fromSelectOpen, setFromSelectOpen] = useState(false);
  const [toSelectOpen, setToSelectOpen] = useState(false);

  // Local state to track user selections (including invalid ones)
  const [localGte, setLocalGte] = useState<string>('');
  const [localLte, setLocalLte] = useState<string>('');

  const { appliedDateRange, updateDateRange, applyFilters } = useFilterSets(
    LOCATION.DISCOVERY
  );
  const triggerRef = useRef<HTMLDivElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Initialize local state from applied range
  useEffect(() => {
    setLocalGte(appliedDateRange.gte || MIN_VOLUME_YEAR_STR);
    setLocalLte(appliedDateRange.lte || MAX_VOLUME_YEAR_STR);
  }, [appliedDateRange.gte, appliedDateRange.lte]);

  // Handle click outside to close dropdown
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        triggerRef.current &&
        !triggerRef.current.contains(event.target as Node) &&
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
        setFromSelectOpen(false);
        setToSelectOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  // Validate date range
  const validateDateRange = (
    gteValue: string,
    lteValue: string
  ): string | null => {
    const fromYear = parseInt(gteValue);
    const toYear = parseInt(lteValue);

    if (fromYear > toYear) {
      return 'From year cannot be greater than Through year';
    }

    return null;
  };

  // Get current validation error using local state
  const getValidationError = (): string | null => {
    return validateDateRange(localGte, localLte);
  };

  // Handle year selection
  const handleYearChange = (type: 'gte' | 'lte', value: string) => {
    let newGte = localGte;
    let newLte = localLte;

    if (type === 'gte') {
      newGte = value;
      setLocalGte(value);
    } else {
      newLte = value;
      setLocalLte(value);
    }

    // Check if the new range is valid
    const validationError = validateDateRange(newGte, newLte);

    // Only update the filter system if the range is valid
    if (!validationError) {
      updateDateRange({
        gte: newGte,
        lte: newLte,
      });
      applyFilters();
    }
  };

  // Toggle dropdown
  const toggleDropdown = () => {
    setIsOpen(!isOpen);
  };

  const validationError = getValidationError();

  return (
    <div className="min-w-36 flex-shrink-0 inline-flex flex-col justify-start items-end gap-1 relative">
      <div
        ref={triggerRef}
        className={`w-full px-4 bg-white cursor-pointer hover:bg-neutral-100 rounded-full outline outline-1
              -outline-offset-1 ${
                validationError ? 'outline-red-400' : 'outline-neutral-300'
              } inline-flex justify-between items-center h-9`}
        onClick={toggleDropdown}
        data-testid="date-dropdown-trigger"
      >
        <div className="flex items-center gap-2 overflow-hidden">
          <div className="w-4 h-4 flex-shrink-0 flex items-center justify-center">
            <Icon
              icon={faCalendarRange}
              className="text-neutral-600 w-4 h-3.5"
              data-testid="calendar-icon"
            />
          </div>
          <div
            className={`max-w-32 text-sm text-neutral-800 font-['Inter'] leading-normal whitespace-nowrap truncate font-semibold `}
          >
            {localGte || '2022'} - {localLte || '2025'}
          </div>
        </div>
        <div className="w-4 h-4 flex items-center justify-center transition-transform duration-300 ml-2">
          <Icon
            icon={faCaretDown}
            className={`w-3 h-3 text-neutral-600  ${
              isOpen ? '-rotate-90' : 'rotate-0'
            }`}
            data-testid="caret-icon"
          />
        </div>
      </div>

      {isOpen &&
        createPortal(
          <div
            ref={dropdownRef}
            className="fixed z-[99999] w-60  bg-white border border-neutral-200 rounded-lg shadow-md p-4"
            style={{
              top: triggerRef.current
                ? triggerRef.current.getBoundingClientRect().bottom + 4
                : 0,
              right: triggerRef.current
                ? window.innerWidth -
                  triggerRef.current.getBoundingClientRect().right
                : 0,
            }}
            data-testid="date-dropdown-menu"
          >
            <div className="flex flex-col gap-4">
              {/* Year Range Selectors - Side by side layout */}
              <div className="pb-2 border-b border-neutral-300">
                <div className="flex items-center justify-center gap-2 mb-2">
                  {/* From Custom Select */}
                  <div className="flex flex-col gap-1 relative">
                    <label className="text-xs font-medium text-neutral-700">
                      From
                    </label>
                    <div className="relative">
                      <button
                        className="w-[90px] px-3 py-2 bg-white border border-neutral-300 rounded-full text-sm flex justify-between items-center"
                        onClick={(e) => {
                          e.stopPropagation();
                          setFromSelectOpen(!fromSelectOpen);
                          setToSelectOpen(false);
                        }}
                        data-testid="date-from-custom-select"
                      >
                        <span>{localGte || '2022'}</span>
                        <Icon
                          icon={faCaretDown}
                          className={`w-2.5 h-2.5 text-neutral-600 transition-transform duration-200 ${
                            fromSelectOpen ? 'rotate-180' : ''
                          }`}
                        />
                      </button>

                      {fromSelectOpen && (
                        <div className="absolute top-full left-0 mt-1 w-full bg-white border border-neutral-200 rounded-lg shadow-md z-[99999]">
                          {VOLUME_2022_2025.map((option) => (
                            <div
                              key={`from-${option.value}`}
                              className={`px-4 py-2 hover:bg-neutral-100 cursor-pointer ${
                                localGte === option.value
                                  ? 'bg-neutral-100'
                                  : ''
                              }`}
                              onClick={() => {
                                handleYearChange('gte', option.value);
                                setFromSelectOpen(false);
                              }}
                              data-testid={`from-year-option-${option.value}`}
                            >
                              <span className="text-sm">{option.label}</span>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="mt-6 text-neutral-500">-</div>

                  {/* Through Custom Select */}
                  <div className="flex flex-col gap-1 relative">
                    <label className="text-xs font-medium text-neutral-700">
                      Through
                    </label>
                    <div className="relative">
                      <button
                        className="w-[90px] px-3 py-2 bg-white border border-neutral-300 rounded-full text-sm flex justify-between items-center"
                        onClick={(e) => {
                          e.stopPropagation();
                          setToSelectOpen(!toSelectOpen);
                          setFromSelectOpen(false);
                        }}
                        data-testid="date-through-custom-select"
                      >
                        <span>{localLte || '2025'}</span>
                        <Icon
                          icon={faCaretDown}
                          className={`w-2.5 h-2.5 text-neutral-600 transition-transform duration-200 ${
                            toSelectOpen ? 'rotate-180' : ''
                          }`}
                        />
                      </button>

                      {toSelectOpen && (
                        <div className="absolute top-full left-0 mt-1 w-full bg-white border border-neutral-200 rounded-lg shadow-md z-[99999]">
                          {VOLUME_2022_2025.map((option) => (
                            <div
                              key={`to-${option.value}`}
                              className={`px-4 py-2 hover:bg-neutral-100 cursor-pointer ${
                                localLte === option.value
                                  ? 'bg-neutral-100'
                                  : ''
                              }`}
                              onClick={() => {
                                handleYearChange('lte', option.value);
                                setToSelectOpen(false);
                              }}
                              data-testid={`through-year-option-${option.value}`}
                            >
                              <span className="text-sm">{option.label}</span>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              {/* Validation Error Display */}
              {validationError && (
                <div className="px-3 py-2 bg-red-50 border border-red-200 rounded-lg">
                  <p className="text-xs text-red-600 font-medium">
                    {validationError}
                  </p>
                </div>
              )}

              {/* Compare Years section - similar to VolumeDateToggle */}
              <div className="pt-1">
                <p className="text-xs font-bold text-neutral-900 leading-5">
                  Year Range
                </p>
                <p className="text-xs font-light text-neutral-600 leading-4">
                  Select the years to include in your results.
                </p>
              </div>
            </div>
          </div>,
          document.body
        )}
    </div>
  );
};

export default DateDropdown;
